/**
 * @file useShopCardActions.ts
 * @description Действия шапки карточки магазина (phase19.28 этап А;
 * uiux-platform-shops §4; PL1, PL15, PL17, В8, В9): «Витрина ↗»,
 * «Кабинет ↗» (вход под владельцем с причиной), «Изменить сведения»,
 * «Отключить / Включить магазин» (с подтверждением, право
 * `PLATFORM_MODERATE_SHOPS`). Возвращает действия данными для
 * `CabinetPageHeader` и состояние окон.
 */

import { ExternalLink, LogIn, Pencil, Power } from "lucide-react";
import { useCallback, useState } from "react";

import { useHasPermission } from "../../auth/hooks";
import type { PageAction } from "../../components/ui/page-actions";
import { useNotify } from "../../components/ui/NotifyProvider";
import { useConfirm } from "../../components/ui/use-confirm";
import {
  IMPERSONATE_PERMISSION,
  SHOPS_MODERATE_PERMISSION,
  platformShops,
  type ShopCard,
} from "../../lib/api-platform-shops";
import { apiErrorText } from "../../lib/platform/shop-errors";

export function useShopCardActions(shop: ShopCard | null, reload: () => void) {
  const notify = useNotify();
  const confirmApi = useConfirm();
  const canModerate = useHasPermission(SHOPS_MODERATE_PERMISSION);
  // Вход читает владельца через «Пользователи» — нужны оба права (ревью В4).
  const canImpersonate = useHasPermission(IMPERSONATE_PERMISSION);
  const canUsers = useHasPermission("PLATFORM_MANAGE_USERS");
  const [editing, setEditing] = useState(false);
  const [cabinetOwnerId, setCabinetOwnerId] = useState<string | null>(null);
  const closeCabinet = useCallback(() => setCabinetOwnerId(null), []);
  const enterCabinet = useCallback((ownerId: string) => setCabinetOwnerId(ownerId), []);

  async function toggleActive(target: ShopCard) {
    const disable = target.isActive;
    const ok = await confirmApi.confirm(
      disable
        ? {
            title: `Отключить магазин «${target.displayName}»?`,
            description:
              "Витрина ответит «Магазин закрыт» (410) и пропадёт из поиска, новые заказы станут невозможны. Включить обратно можно здесь же.",
            action: "Отключить магазин",
            danger: true,
          }
        : { title: `Включить магазин «${target.displayName}»?`, action: "Включить магазин" },
    );
    if (!ok) return;
    try {
      await platformShops.setActive(target.id, !disable);
      notify.success(disable ? "Магазин отключён" : "Магазин включён");
      reload();
    } catch (err) {
      notify.error(apiErrorText(err, "Не удалось изменить состояние магазина"));
    }
  }

  const owner = shop?.owners[0] ?? null;
  const secondary: PageAction[] = [];
  const menu: PageAction[] = [];
  if (shop?.primaryHost) {
    const url = `https://${shop.primaryHost}`;
    secondary.push({ key: "site", label: "Витрина ↗", icon: ExternalLink, run: () => window.open(url, "_blank", "noopener") });
  }
  // Приглашение ещё не принято — у владельца нет кабинета, входить некуда.
  if (shop && canImpersonate && canUsers && owner && shop.ownerInvitation.status !== "pending") {
    secondary.push({ key: "cabinet", label: "Кабинет ↗", icon: LogIn, run: () => enterCabinet(owner.id) });
  }
  // Право раздела (PLATFORM_MANAGE_SHOPS) проверяет маршрут — здесь не дублируем.
  if (shop) menu.push({ key: "edit", label: "Изменить сведения", icon: Pencil, run: () => setEditing(true) });
  if (shop && canModerate) {
    menu.push({
      key: "active",
      label: shop.isActive ? "Отключить магазин" : "Включить магазин",
      icon: Power,
      danger: shop.isActive,
      run: () => void toggleActive(shop),
    });
  }

  return { secondary, menu, confirmApi, editing, setEditing, cabinetOwnerId, closeCabinet, enterCabinet };
}
